import type { MatrixClient, Room } from 'matrix-js-sdk';
import { RoomStateEvent } from 'matrix-js-sdk';

type Listener = () => void;

export interface PowerLevels {
  ban: number;
  invite: number;
  kick: number;
  redact: number;
  send: number;
  sendStandalone: number;
  stateDefault: number;
  usersDefault: number;
  users: Record<string, number>;
}

/**
 * Store für die Power Levels eines einzelnen Rooms.
 */
class PowerLevelsStore {
  private listeners = new Set<Listener>();
  private room: Room | null = null;
  private powerLevels: PowerLevels | null = null;
  private eventId: string | undefined = undefined;
  private isSetup = false;

  constructor(
    private readonly roomId: string,
    private readonly client: MatrixClient,
  ) {
    this.setup();
  }

  private setup(): void {
    if (this.isSetup) return;

    this.room = this.client.getRoom(this.roomId);
    if (!this.room) {
      this.powerLevels = null;
      return;
    }

    this.updatePowerLevels();
    this.room.on(RoomStateEvent.Update, this.handleStateChange);
    this.isSetup = true;
  }

  private handleStateChange = (): void => {
    this.updatePowerLevels();
    this.emitChange();
  };

  private updatePowerLevels(): void {
    if (!this.room) {
      this.powerLevels = null;
      this.eventId = undefined;
      return;
    }

    const powerLevelEvent = this.room.currentState.getStateEvents('m.room.power_levels', '');
    if (!powerLevelEvent) {
      this.powerLevels = null;
      this.eventId = undefined;
      return;
    }

    const currentEventId = powerLevelEvent.getId();

    // Nur aktualisieren wenn sich die Event-ID geändert hat
    if (this.eventId !== currentEventId) {
      this.eventId = currentEventId;
      this.powerLevels = powerLevelEvent.getContent() as PowerLevels;
    }
  }

  getSnapshot = (): PowerLevels | null => {
    return this.powerLevels;
  };

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private emitChange(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Cleanup-Funktion zum Entfernen von Event-Listenern.
   */
  destroy(): void {
    if (this.room && this.isSetup) {
      this.room.removeListener(RoomStateEvent.Update, this.handleStateChange);
    }
    this.listeners.clear();
    this.isSetup = false;
  }
}

/**
 * Store-Manager für Power Levels.
 * Verwaltet Stores pro Room und kümmert sich um Cleanup.
 */
class PowerLevelsStoreManager {
  private stores = new Map<string, PowerLevelsStore>();
  private client: MatrixClient | null = null;

  setClient(client: MatrixClient | null): void {
    // Cleanup alte Stores
    if (this.client !== client) {
      this.cleanup();
    }
    this.client = client;
  }

  getStore(roomId: string): PowerLevelsStore | null {
    if (!this.client) return null;

    if (!this.stores.has(roomId)) {
      const store = new PowerLevelsStore(roomId, this.client);
      this.stores.set(roomId, store);
    }

    return this.stores.get(roomId) ?? null;
  }

  /**
   * Cleanup nicht mehr verwendeter Stores.
   */
  cleanup(): void {
    for (const store of this.stores.values()) {
      store.destroy();
    }
    this.stores.clear();
  }

  /**
   * Cleanup eines spezifischen Stores.
   */
  cleanupStore(roomId: string): void {
    const store = this.stores.get(roomId);
    if (store) {
      store.destroy();
      this.stores.delete(roomId);
    }
  }
}

// Singleton-Instanz
export const powerLevelsStoreManager = new PowerLevelsStoreManager();
